import React, { useState, useRef, useEffect } from "react";
import { FiSend, FiX, FiRefreshCw } from "react-icons/fi";
import { BsRobot, BsStars } from "react-icons/bs";
import { ChatMessage, sendChatbotQuery } from "api/ai";
import { ChatMessageItem, TypingIndicator } from "./ChatMessageItem";

interface ChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const INITIAL_WELCOME_MESSAGE: ChatMessage = {
  id: "welcome-1",
  sender: "bot",
  text: "Hello! 👋 I'm your official **Express Chart Note AI Assistant**.\n\nHow can I help you today with clinical SOAP note creation, anatomical location picking, theme customization, or subscription plans?",
  timestamp: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
};

const SUGGESTION_CHIPS = [
  "What subscription plans are available?",
  "How do I create a SOAP note in the wizard?",
  "How to export PDF or print reports?",
  "What happens when my 14-day trial expires?",
];

export const ChatModal: React.FC<ChatModalProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_WELCOME_MESSAGE]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen, messages, loading]);

  if (!isOpen) return null;

  const handleSendMessage = async (textToSend?: string) => {
    const text = (textToSend || inputValue).trim();
    if (!text || loading) return;

    const userTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      sender: "user",
      text: text,
      timestamp: userTime,
    };

    setMessages((prev) => [...prev, userMsg]);
    if (!textToSend) setInputValue("");
    setLoading(true);

    try {
      const responseText = await sendChatbotQuery(text);
      const botTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      const botMsg: ChatMessage = {
        id: `bot-${Date.now()}`,
        sender: "bot",
        text: responseText,
        timestamp: botTime,
      };

      setMessages((prev) => [...prev, botMsg]);
    } catch (err) {
      const errorTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      setMessages((prev) => [
        ...prev,
        {
          id: `err-${Date.now()}`,
          sender: "bot",
          text: "Sorry, I encountered an unexpected error. Please connect with customer support.",
          timestamp: errorTime,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearHistory = () => {
    setMessages([INITIAL_WELCOME_MESSAGE]);
  };

  return (
    <div className="fixed bottom-24 right-6 z-50 flex w-[90vw] max-w-[420px] h-[580px] max-h-[80vh] flex-col rounded-[20px] border border-borderPrimary bg-surfacePrimary shadow-2xl transition-all duration-300 dark:border-navy-600 dark:bg-navy-800">
      {/* Modal Header */}
      <div className="flex items-center justify-between border-b border-borderPrimary bg-surfaceSecondary px-4 py-3.5 rounded-t-[20px] dark:border-navy-600 dark:bg-navy-700">
        <div className="flex items-center gap-3">
          <div className="relative flex h-9 w-9 items-center justify-center rounded-full bg-brand-500 text-white shadow-md">
            <BsRobot className="h-5 w-5" />
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-white"></span>
          </div>
          <div>
            <h3 className="font-poppins text-sm font-bold text-textPrimary dark:text-white flex items-center gap-1.5">
              Express Chart Note AI
              <BsStars className="h-3.5 w-3.5 text-brand-500" />
            </h3>
            <p className="text-[11px] font-medium text-textSecondary dark:text-gray-300">
              Online · Clinical Reference Assistant
            </p>
          </div>
        </div>

        {/* Action controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleClearHistory}
            title="Reset conversation"
            className="rounded-lg p-1.5 text-textSecondary hover:bg-brand-50 hover:text-brand-500 dark:text-gray-300 dark:hover:bg-navy-600 dark:hover:text-white transition-colors"
          >
            <FiRefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            title="Close Assistant"
            className="rounded-lg p-1.5 text-textSecondary hover:bg-brand-50 hover:text-brand-500 dark:text-gray-300 dark:hover:bg-navy-600 dark:hover:text-white transition-colors"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Message List Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 slidebar-scroller">
        {messages.map((msg) => (
          <ChatMessageItem key={msg.id} message={msg} />
        ))}

        {loading && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestion Chips */}
      {messages.length <= 2 && !loading && (
        <div className="px-4 pb-2">
          <p className="text-[11px] font-semibold text-textSecondary mb-1.5 dark:text-gray-400">
            Suggested questions:
          </p>
          <div className="flex flex-wrap gap-1.5">
            {SUGGESTION_CHIPS.map((chip, idx) => (
              <button
                key={idx}
                onClick={() => handleSendMessage(chip)}
                className="rounded-full border border-brand-200 bg-brand-50/60 px-3 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-50 hover:border-brand-500 dark:border-navy-500 dark:bg-navy-700 dark:text-brand-100 dark:hover:bg-navy-600 transition-colors text-left"
              >
                {chip}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="border-t border-borderPrimary bg-surfacePrimary p-3 rounded-b-[20px] dark:border-navy-600 dark:bg-navy-800">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          className="flex items-end gap-2"
        >
          <textarea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your question here..."
            rows={1}
            disabled={loading}
            className="flex-1 resize-none rounded-xl border border-borderPrimary bg-surfaceSecondary p-2.5 text-xs text-textPrimary placeholder:text-textMuted focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:placeholder:text-gray-400"
            style={{ maxHeight: "80px" }}
          />

          <button
            type="submit"
            disabled={!inputValue.trim() || loading}
            aria-label="Send Message"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-500 text-white shadow-md transition-all hover:bg-brand-500/80 active:bg-brand-500/70 disabled:opacity-40 disabled:hover:bg-brand-500 dark:bg-brand-500 dark:hover:bg-brand-500/80 dark:active:bg-brand-500/70"
          >
            <FiSend className="h-4 w-4" />
          </button>
        </form>
        <p className="mt-1.5 text-center text-[10px] text-textMuted dark:text-gray-500">
          Powered by Gemini AI · Press Enter to send, Shift+Enter for new line
        </p>
      </div>
    </div>
  );
};
